import { Elm } from "../Main.elm";
// Feuille du socle visuel, commune aux applications : tokens du thème Asria et partials en couches.
import "../../../shared/sass/main.scss";
import { garderParcoursEtPartir } from "./parcours.js";

// Préférences d'affichage gardées par le navigateur (spec 04 E-3.5) : le document est passé tel quel à Elm, qui le
// lit champ par champ. Un navigateur qui refuse l'accès à localStorage ne doit pas empêcher le démarrage.
const clePreferences = "asria-crm.backoffice.preferences";

function lirePreferences() {
  try {
    return window.localStorage.getItem(clePreferences);
  } catch {
    return null;
  }
}

// Parcours de connexion en cours (CRM-8) : `state`, `code_verifier` et page à retrouver, gardés dans sessionStorage
// le temps de l'aller-retour vers Keycloak. Il est effacé dès qu'il est lu, à chaque chargement de la page : il ne
// sert qu'une fois. Aucun jeton n'est jamais écrit, ni ici ni ailleurs : les jetons restent dans la mémoire d'Elm.
const cleParcours = "asria-crm.backoffice.connexion";

function lireEtEffacerLeParcours() {
  try {
    const garde = window.sessionStorage.getItem(cleParcours);
    window.sessionStorage.removeItem(cleParcours);
    return garde === null ? null : JSON.parse(garde);
  } catch {
    return null;
  }
}

// La configuration d'exécution (config.json) est générée au build depuis l'environnement.
fetch("/config.json")
  .then((reponse) => reponse.json())
  .catch(() => null)
  .then((config) => {
    // API simulée des écrans « sans back » (CRM-172) : mode démo en préprod (config.json) et en local. Chargée
    // par import() : hors démo, ni elle ni ses fixtures ne sont lues.
    const modeDemo = config?.modeDemo === true || import.meta.env.DEV;
    return (
      modeDemo
        ? import("../../../shared/api-simulee/demo.js").then((demo) =>
            demo.demarrerLApiSimulee({
              config,
              enDeveloppement: import.meta.env.DEV,
            }),
          )
        : Promise.resolve()
    ).then(() => config);
  })
  .then((config) => {
    const application = Elm.Main.init({
      flags: {
        ...config,
        preferences: lirePreferences(),
        connexion: lireEtEffacerLeParcours(),
      },
    });
    // Port « authentification » : des octets aléatoires de qualité cryptographique, qu'Elm ne sait pas tirer.
    application.ports.demanderAlea.subscribe((nombre) => {
      application.ports.aleaRecu.send(
        Array.from(window.crypto.getRandomValues(new Uint8Array(nombre))),
      );
    });
    // Port « authentification » : le parcours à garder, puis le départ vers Keycloak, dans le même geste (l'écriture
    // précède toujours la navigation). Si le navigateur refuse l'écriture, le retour de Keycloak ne trouvera pas son
    // parcours, et l'écran le dira.
    application.ports.garderParcoursEtPartir.subscribe((depart) => {
      garderParcoursEtPartir(window.sessionStorage, window.location, cleParcours, depart);
    });
    // Un navigateur qui refuse l'écriture (quota, accès interdit) garde la bascule pour la session.
    application.ports.ecrirePreferences.subscribe((preferences) => {
      try {
        window.localStorage.setItem(
          clePreferences,
          JSON.stringify(preferences),
        );
      } catch {
        // Rien à faire : la préférence vaut pour la session.
      }
    });
    // Port « erreurs » : la console, en attendant la supervision de l'US 0.12.
    application.ports.signalerErreur.subscribe((message) =>
      console.error(message),
    );
    // Favicon de la marque de l'instance : l'adresse a été validée par Elm (https, origine admise).
    application.ports.poserFavicon.subscribe((adresse) => {
      const lien =
        document.querySelector('link[rel="icon"]') ??
        document.createElement("link");
      lien.rel = "icon";
      lien.href = adresse;
      document.head.appendChild(lien);
    });
    // Langue du document : celle du catalogue de la session (CRM-335), dite par Elm quand elle change.
    application.ports.poserLangue.subscribe((langue) => {
      document.documentElement.lang = langue;
    });
  });

// Ctrl K et Cmd K appartiennent à la recherche globale (CRM-97) : ces lignes ne font que retenir le navigateur, qui
// en ferait un raccourci de sa barre d'adresse. La décision et le focus sont pris en Elm (Navigation.Update).
window.addEventListener("keydown", (evenement) => {
  const k =
    typeof evenement.key === "string" && evenement.key.toLowerCase() === "k";
  if (
    k &&
    (evenement.ctrlKey || evenement.metaKey) &&
    !evenement.altKey &&
    !evenement.shiftKey
  )
    evenement.preventDefault();
});
